import { Fragment, memo, type ReactNode } from 'react';
import { cn } from '@cyphex/ui';
import type { RichDocument, RichMark, RichNode } from '@cyphex/types';
import { AppLink } from '@/components/common/AppLink';
// Imported by path, not through the feature barrel: the barrel pulls ChannelPage
// and would drag the whole channels route into the chat chunk.
import { ChannelRefLink } from '@/features/channels/components/ChannelRefLink';
import { CodeBlock } from './CodeBlock';

interface RichTextProps {
  doc: RichDocument | null;
  /** Plain-text fallback when the server sent no rich body. */
  fallback?: string | null;
  className?: string;
}

const MARK_CLASS: Record<RichMark['type'], string> = {
  bold: 'font-semibold',
  italic: 'italic',
  underline: 'underline underline-offset-2',
  strike: 'line-through opacity-70',
  code: 'rounded-xs bg-secondary px-1 py-0.5 font-mono text-base text-secondary-foreground',
  link: 'font-medium text-primary hover:underline',
  highlight: 'rounded-xs bg-warning-subtle px-0.5 text-warning-foreground',
};

function applyMarks(content: ReactNode, marks: readonly RichMark[] | undefined, key: string): ReactNode {
  if (!marks || marks.length === 0) return content;
  const link = marks.find((mark) => mark.type === 'link');
  const classes = marks.map((mark) => MARK_CLASS[mark.type]).join(' ');
  if (link?.attrs?.href) {
    // Into the app: a route change. Out of it: a new window. See AppLink.
    return (
      <AppLink key={key} href={link.attrs.href} className={cn(classes)}>
        {content}
      </AppLink>
    );
  }
  return (
    <span key={key} className={cn(classes)}>
      {content}
    </span>
  );
}

function renderNodes(nodes: readonly RichNode[] | undefined, prefix: string): ReactNode {
  if (!nodes) return null;
  return nodes.map((node, index) => <Fragment key={`${prefix}.${index}`}>{renderNode(node, `${prefix}.${index}`)}</Fragment>);
}

function renderNode(node: RichNode, key: string): ReactNode {
  switch (node.type) {
    case 'text':
      return applyMarks(node.text ?? '', node.marks, key);
    case 'hard_break':
      return <br />;
    case 'mention': {
      const label = node.attrs?.label ?? 'unknown';
      // `#channel` is a cross-reference, not a ping: it navigates and notifies
      // nobody, so it must not look like an @mention.
      if (node.attrs?.mentionKind === 'channel_ref') {
        return (
          <ChannelRefLink
            label={label}
            href={node.attrs.href}
            className="rounded-xs bg-secondary px-1 font-medium text-secondary-foreground hover:text-primary"
          />
        );
      }
      return (
        <span className="rounded-xs bg-primary-subtle px-1 font-medium text-primary">
          @{label}
        </span>
      );
    }
    case 'emoji': {
      // Custom emoji carry an image; unicode emoji are just text.
      const src = node.attrs?.src;
      const shortcode = node.attrs?.shortcode;
      if (src !== undefined && src !== '') {
        return (
          <img
            src={src}
            alt={shortcode === undefined ? 'emoji' : `:${shortcode}:`}
            title={shortcode === undefined ? undefined : `:${shortcode}:`}
            className="inline-block h-[1.25em] w-[1.25em] align-text-bottom"
            loading="lazy"
          />
        );
      }
      return <span aria-label={shortcode}>{node.text ?? shortcode}</span>;
    }
    case 'paragraph':
      return <p className="whitespace-pre-wrap break-words">{renderNodes(node.content, key)}</p>;
    case 'heading': {
      const level = node.attrs?.level ?? 2;
      const size = level === 1 ? 'text-2xl' : level === 2 ? 'text-xl' : 'text-lg';
      return <p className={cn('font-semibold', size)}>{renderNodes(node.content, key)}</p>;
    }
    case 'bullet_list':
      return <ul className="list-disc space-y-0.5 pl-5">{renderNodes(node.content, key)}</ul>;
    case 'ordered_list':
      return <ol className="list-decimal space-y-0.5 pl-5">{renderNodes(node.content, key)}</ol>;
    case 'list_item':
      return <li>{renderNodes(node.content, key)}</li>;
    case 'blockquote':
      return (
        <blockquote className="border-l-2 border-border-strong pl-3 text-muted-foreground">
          {renderNodes(node.content, key)}
        </blockquote>
      );
    case 'code_block':
      return (
        <CodeBlock
          code={node.content?.map((child) => child.text ?? '').join('') ?? ''}
          language={node.attrs?.language ?? ''}
        />
      );
    case 'horizontal_rule':
      return <hr className="my-2 border-border" />;
    default:
      return renderNodes(node.content, key);
  }
}

/** Renders the canonical ProseMirror body. Memoised — bubbles never re-render on scroll. */
export const RichText = memo(function RichText({ doc, fallback, className }: RichTextProps) {
  if (!doc) {
    return fallback ? (
      <p className={cn('whitespace-pre-wrap break-words text-md text-foreground', className)}>{fallback}</p>
    ) : null;
  }
  return (
    <div className={cn('space-y-1.5 text-md leading-[1.45rem] text-foreground', className)}>
      {renderNodes(doc.content, 'n')}
    </div>
  );
});
